"use client"

import { BlockchainsMoleculeReatom } from "@/app/blockchains/blockchain-molecule.reatom"
import { WalletConnectMoleculeReatom } from "@/app/wallet-connect/store.reatom"
import {
  Blockchain,
  type SolanaBlockchainConfig,
} from "@anaconda/core/blockchain/types"
import {
  useSignTransaction,
  useWallets as useSolanaWallets,
} from "@privy-io/react-auth/solana"
import { createReatomSolanaContainer } from "@rarible_int/anaconda_sdk/solana"
import { useWrap } from "@reatom/react"
import { useMolecule } from "bunshi/react"
import { useEffect } from "react"
import {
  type PrivySolanaChain,
  createPrivySolanaSigner,
} from "./privy-solana-signer"
import { SolanaSdkContainerMoleculeReatom } from "./solana-sdk-container-molecule.reatom"

function getPrivySolanaChain(
  chainId: string | number | undefined,
): PrivySolanaChain {
  return chainId === "solana:devnet" ? "solana:devnet" : "solana:mainnet"
}

function getConnectedSolanaWallet(
  wallets: ReturnType<typeof useSolanaWallets>["wallets"],
  solanaAddress: string | undefined,
) {
  if (!solanaAddress) return undefined

  return (
    wallets.find((wallet) => wallet.address === solanaAddress) ??
    wallets.find((wallet) => wallet.standardWallet.name === "Privy") ??
    wallets[0]
  )
}

export function useSolanaSdkProviderReatom() {
  const { blockchainsMap } = useMolecule(BlockchainsMoleculeReatom)
  const blockchainsByKey = blockchainsMap()
  const solanaConfig = blockchainsByKey[Blockchain.SOLANA] as
    | SolanaBlockchainConfig
    | undefined

  const { solanaAddress: solanaAddressAtom } = useMolecule(
    WalletConnectMoleculeReatom,
  )
  const solanaAddress = solanaAddressAtom()

  const { wallets: solanaWallets } = useSolanaWallets()
  const { signTransaction } = useSignTransaction()
  const connectedWallet = getConnectedSolanaWallet(solanaWallets, solanaAddress)
  const privySolanaChain = getPrivySolanaChain(solanaConfig?.chainId)

  const { updateSdk, clearSdk } = useMolecule(SolanaSdkContainerMoleculeReatom)

  const initSdk = useWrap(async () => {
    if (!solanaAddress || !connectedWallet || !solanaConfig) {
      clearSdk()
      return
    }

    // The configured Helius key is Origin-locked to the deployed rarible hosts,
    // so it 403s from localhost. Allow an override for local development; unset
    // everywhere else, which keeps the deployed behaviour identical.
    const rpcUrl =
      process.env.NEXT_PUBLIC_SOLANA_RPC_URL ||
      solanaConfig.rpcUrls[0]?.url ||
      "https://api.mainnet-beta.solana.com"

    const signer = createPrivySolanaSigner({
      address: solanaAddress,
      wallet: connectedWallet,
      chain: privySolanaChain,
      signTransaction,
    })

    const sdk = createReatomSolanaContainer({
      signer,
      rpcUrl,
      unionApi: solanaConfig.unionApi,
      apiKey: solanaConfig.apiKey,
      marketProgramAddress: solanaConfig.marketProgramAddress,
      marketIdentifier: solanaConfig.marketIdentifier,
      primaryCurrency: solanaConfig.primaryCurrency,
    })

    updateSdk(sdk, "privy-solana")
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: provider effect
  useEffect(() => {
    void initSdk()
  }, [
    solanaAddress,
    connectedWallet?.address,
    solanaConfig?.unionApi,
    solanaConfig?.apiKey,
    solanaConfig?.rpcUrls?.[0]?.url,
    solanaConfig?.marketProgramAddress,
    solanaConfig?.chainId,
    privySolanaChain,
    signTransaction,
    initSdk,
  ])

  return null
}
